import {
  Camera,
  Check,
  ClipboardCheck,
  DollarSign,
  Package,
  Truck,
  Users,
  Plus,
  X,
  AlertCircle,
  Calendar,
  Clock,
  TrendingUp,
  ChevronRight,
  Circle,
  CheckCircle,
  Loader2,
  Eye,
  FileText,
  MoreVertical,
  UserPlus,
  Activity,
  BarChart3,
  Smartphone,
  Hourglass,
} from "lucide-react";
import { useProjectManagementContext } from "../context";
import ProjectSectionShell from "./ProjectSectionShell";
import ReceiptPreviewModal, { type ReceiptPreview } from "../../../components/ReceiptPreviewModal";
import { useState } from "react";

const normalizeExpenseCategory = (value: unknown) => String(value ?? "").trim().toLowerCase();

const looksLikeContingencyCategory = (category: unknown) =>
  /kontingensi|contingency|darurat|cadangan/i.test(String(category || ""));

const allocationName = (alloc: any) => String(alloc?.name ?? alloc?.category ?? "").trim();

const expenseMatchesAllocation = (expenseCategory: unknown, alloc: any) => {
  const category = normalizeExpenseCategory(expenseCategory);
  const name = normalizeExpenseCategory(allocationName(alloc));
  return category !== "" && name !== "" && category === name;
};

const formatIdr = (amount: number) =>
  new Intl.NumberFormat("id-ID", { style: "currency", currency: "IDR", maximumFractionDigits: 0 }).format(amount);

const ExecutionSection = () => {
  const {
    baselineData,
    executionData,
    handleToggleMaterial,
    materialBudget,
    setActiveEvidence,
    setActiveTaskId,
    handleAddExpenseApi,
    setIsEvidenceModalOpen,
    setIsTeamModalOpen,
    taskTeams,
    totalCalculatedExpense,
    allProjects,
    activeProjectId,
  } = useProjectManagementContext() as any;

  const activeProject = allProjects?.find((p: any) => p.id === activeProjectId);
  const isExecutionLocked = activeProject && activeProject.status !== 'active';
  const contingency = executionData?.contingency || {};
  const allocatedContingency = Number(contingency.allocated_amount ?? contingency.nominal ?? 0);
  const remainingContingency = Number(contingency.remaining_amount ?? contingency.remaining ?? 0);
  const spentContingency = Number(contingency.spent_amount ?? contingency.spent ?? 0);
  const hasContingencyBudget = allocatedContingency > 0;
  const isContingencyFormLocked = Boolean(isExecutionLocked) || !hasContingencyBudget || remainingContingency <= 0;
  const contingencyLockReason = isExecutionLocked
    ? "Proyek belum aktif atau sedang ditunda. Pencatatan kontingensi terkunci."
    : !hasContingencyBudget
      ? "RAB tidak mengalokasikan kontingensi."
      : remainingContingency <= 0
        ? "Sisa dana kontingensi habis. Ajukan perubahan RAB jika masih perlu belanja darurat."
        : "";
  const contingencyHistory = (executionData.contingencyExpenses?.length
    ? executionData.contingencyExpenses
    : (executionData.actualExpenses || []).filter((exp: any) =>
        /kontingensi|contingency|darurat|cadangan/i.test(String(exp.category || ""))
      ));


  const [expandedTasks, setExpandedTasks] = useState<number[]>([]);
  const [expenseKindTab, setExpenseKindTab] = useState<'operational' | 'contingency'>('operational');
  const [expenseLabel, setExpenseLabel] = useState("");
  const [expenseAmount, setExpenseAmount] = useState("");
  const [expenseDesc, setExpenseDesc] = useState("");
  const [expenseReceipt, setExpenseReceipt] = useState<File | null>(null);
  const [expenseDate, setExpenseDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [rabLabel, setRabLabel] = useState("");
  const [rabAmount, setRabAmount] = useState("");
  const [rabDesc, setRabDesc] = useState("");
  const [rabReceipt, setRabReceipt] = useState<File | null>(null);
  const [rabDate, setRabDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [rabCategory, setRabCategory] = useState("");
  const [receiptPreview, setReceiptPreview] = useState<ReceiptPreview>(null);
  const [activeTab, setActiveTab] = useState<'vendors' | 'inventory'>('vendors');
  const [sectionTab, setSectionTab] = useState<'tasks' | 'expenses' | 'resources'>('tasks');

  const operationalAllocations = (baselineData.allocation || []).filter(
    (alloc: any) => !looksLikeContingencyCategory(allocationName(alloc))
  );
  const selectedRabAlloc = operationalAllocations.find((alloc: any) =>
    normalizeExpenseCategory(allocationName(alloc)) === normalizeExpenseCategory(rabCategory)
  ) || operationalAllocations[0];
  const selectedRabName = selectedRabAlloc ? allocationName(selectedRabAlloc) : "";
  const selectedRabBudget = selectedRabAlloc
    ? ((baselineData.budget || 0) * Number(selectedRabAlloc.percentage || 0)) / 100
    : 0;
  const selectedRabSpent = (executionData.actualExpenses || [])
    .filter((e: any) => expenseMatchesAllocation(e.category, selectedRabAlloc || {}))
    .reduce((sum: number, e: any) => sum + Number(e.amount || 0), 0);
  const selectedRabRemaining = selectedRabBudget - selectedRabSpent;
  const rabAmountNumber = Number(rabAmount) || 0;
  const isRabOverBudget = selectedRabAlloc && rabAmountNumber > 0 && (selectedRabSpent + rabAmountNumber) > selectedRabBudget;
  const isRabFormLocked = Boolean(isExecutionLocked) || operationalAllocations.length === 0;
  const rabLockReason = isExecutionLocked
    ? "Proyek belum aktif atau sedang ditunda. Pencatatan pengeluaran RAB terkunci."
    : operationalAllocations.length === 0
      ? "Belum ada pos RAB selain kontingensi. Atur alokasi di Perencanaan."
      : "";
  const operationalHistory = (executionData.actualExpenses || []).filter((exp: any) =>
    operationalAllocations.some((alloc: any) => expenseMatchesAllocation(exp.category, alloc))
  );

  const totalBudget = baselineData?.budget || 0;

  const toggleTaskExpand = (taskId: number) => {
    setExpandedTasks(prev =>
      prev.includes(taskId) ? prev.filter(id => id !== taskId) : [...prev, taskId]
    );
  };

  const getTaskStatus = (progress: number) => {
    if (progress === 100) return { label: 'Selesai', color: 'emerald', icon: CheckCircle };
    if (progress > 0) return { label: 'Berjalan', color: 'blue', icon: Clock };
    return { label: 'Belum Mulai', color: 'slate', icon: Hourglass };
  };

  const getProgressColor = (progress: number) => {
    if (progress === 100) return 'from-emerald-400 to-emerald-600';
    if (progress > 50) return 'from-blue-400 to-blue-600';
    if (progress > 0) return 'from-amber-400 to-amber-600';
    return 'from-slate-300 to-slate-400';
  };

  const calculateTotalTasks = () => {
    return baselineData.tasks.length;
  };

  const calculateCompletedTasks = () => {
    return baselineData.tasks.filter((t: any) =>
      (executionData.taskProgress[t.id] || 0) === 100
    ).length;
  };

  const calculateOverallProgress = () => {
    return baselineData.progress || 0;
  };

  const handleAddExpense = async () => {
    if (isContingencyFormLocked) return;
    if (expenseLabel && expenseAmount && expenseDesc && expenseReceipt && Number(expenseAmount) > 0) {
      if (handleAddExpenseApi) {
        const saved = await handleAddExpenseApi(
          expenseLabel.trim(),
          Number(expenseAmount),
          expenseDesc.trim(),
          expenseReceipt,
          expenseDate,
          { kind: "contingency" },
        );
        if (saved) {
          setExpenseLabel("");
          setExpenseAmount("");
          setExpenseDesc("");
          setExpenseReceipt(null);
          setExpenseDate(new Date().toISOString().slice(0, 10));
        }
      }
    }
  };

  const handleAddRabExpense = async () => {
    if (isRabFormLocked || !selectedRabName) return;
    if (rabLabel && rabAmount && rabDesc && Number(rabAmount) > 0) {
      if (handleAddExpenseApi) {
        const saved = await handleAddExpenseApi(
          rabLabel.trim(),
          Number(rabAmount),
          rabDesc.trim(),
          rabReceipt,
          rabDate,
          { kind: "operational", category: selectedRabName },
        );
        if (saved) {
          setRabLabel("");
          setRabAmount("");
          setRabDesc("");
          setRabReceipt(null);
          setRabDate(new Date().toISOString().slice(0, 10));
        }
      }
    }
  };

  return (
    <>
    <ProjectSectionShell
      eyebrow="Kontrol Pelaksanaan"
      title="Monitoring Eksekusi dari Mobile App"
      description="Web dipakai provider/PM untuk memantau progress, evidence, issue lapangan, resource, dan expense. Input progress utama dilakukan tukang/serviceman dari aplikasi mobile."
      icon={<ClipboardCheck size={14} />}
      tone="emerald"
    >
      <div className="space-y-6">
        <div className="rounded-2xl border border-blue-100 bg-blue-50 p-4">
          <div className="flex flex-col gap-3 md:flex-row md:items-start">
            <div className="rounded-xl bg-blue-600 p-3 text-white">
              <Smartphone className="h-5 w-5" />
            </div>
            <div>
              <h3 className="font-extrabold text-slate-800">
                Sumber progress: Mobile App Tukang/Serviceman
              </h3>
              <p className="mt-1 text-sm leading-6 text-slate-600">
                Slider progress di web dinonaktifkan. Progress, evidence, dan catatan lapangan dianggap masuk dari mobile app,
                lalu web menjadi dashboard monitoring untuk provider, arsitek/PM, dan admin.
              </p>
            </div>
          </div>
        </div>

        {/* Enhanced Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-white border border-slate-200 rounded-2xl p-5 hover:shadow-lg transition-all duration-300">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm text-slate-500 font-medium">Total Anggaran Proyek</p>
                <p className="text-2xl font-bold text-slate-800 mt-1">
                  {new Intl.NumberFormat("id-ID", {
                    style: "currency",
                    currency: "IDR",
                    maximumFractionDigits: 0,
                  }).format(totalBudget)}
                </p>
              </div>
              <div className="p-3 bg-blue-50 rounded-xl">
                <DollarSign className="w-5 h-5 text-blue-600" />
              </div>
            </div>
          </div>

          <div className="bg-white border border-slate-200 rounded-2xl p-5 hover:shadow-lg transition-all duration-300">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm text-slate-500 font-medium">Terpakai</p>
                <p className={`text-2xl font-bold mt-1 ${totalCalculatedExpense > totalBudget ? 'text-red-600' : 'text-blue-600'
                  }`}>
                  {new Intl.NumberFormat("id-ID", {
                    style: "currency",
                    currency: "IDR",
                    maximumFractionDigits: 0,
                  }).format(totalCalculatedExpense)}
                </p>
              </div>
              <div className="p-3 bg-red-50 rounded-xl">
                <TrendingUp className={`w-5 h-5 ${totalCalculatedExpense > totalBudget ? 'text-red-600' : 'text-blue-600'
                  }`} />
              </div>
            </div>
          </div>

          <div className="bg-white border border-slate-200 rounded-2xl p-5 hover:shadow-lg transition-all duration-300">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm text-slate-500 font-medium">Sisa Saldo</p>
                <p className={`text-2xl font-bold mt-1 ${totalBudget - totalCalculatedExpense < 0 ? 'text-red-600' : 'text-emerald-600'
                  }`}>
                  {new Intl.NumberFormat("id-ID", {
                    style: "currency",
                    currency: "IDR",
                    maximumFractionDigits: 0,
                  }).format(totalBudget - totalCalculatedExpense)}
                </p>
              </div>
              <div className="p-3 bg-emerald-50 rounded-xl">
                <Package className={`w-5 h-5 ${totalBudget - totalCalculatedExpense < 0 ? 'text-red-600' : 'text-emerald-600'
                  }`} />
              </div>
            </div>
            <div className="mt-3">
              <div className="w-full bg-slate-200 rounded-full h-1.5">
                <div
                  className={`h-1.5 rounded-full transition-all duration-500 ${totalBudget > 0
                    ? (totalCalculatedExpense / totalBudget) > 0.9
                      ? 'bg-red-500'
                      : 'bg-emerald-500'
                    : 'bg-slate-300'
                    }`}
                  style={{ width: `${Math.min((totalCalculatedExpense / totalBudget) * 100, 100)}%` }}
                />
              </div>
            </div>
          </div>

          <div className="bg-white border border-slate-200 rounded-2xl p-5 hover:shadow-lg transition-all duration-300">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm text-slate-500 font-medium">Overall Progress</p>
                <p className="text-2xl font-bold text-slate-800 mt-1">
                  {calculateOverallProgress().toFixed(1)}%
                </p>
              </div>
              <div className="p-3 bg-purple-50 rounded-xl">
                <BarChart3 className="w-5 h-5 text-purple-600" />
              </div>
            </div>
            <div className="mt-3 flex items-center gap-2">
              <span className="text-xs text-slate-500">
                {calculateCompletedTasks()} dari {calculateTotalTasks()} tugas selesai
              </span>
            </div>
          </div>
        </div>

        <div className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-1 rounded-2xl border border-slate-200 bg-slate-100 p-1">
            <button
              type="button"
              onClick={() => setSectionTab('tasks')}
              className={`inline-flex items-center justify-center gap-2 rounded-xl px-3 py-2.5 text-sm font-bold transition-all ${sectionTab === 'tasks' ? 'bg-white text-emerald-700 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
            >
              <ClipboardCheck className="w-4 h-4" />
              Daftar Pekerjaan
            </button>
            <button
              type="button"
              onClick={() => setSectionTab('expenses')}
              className={`inline-flex items-center justify-center gap-2 rounded-xl px-3 py-2.5 text-sm font-bold transition-all ${sectionTab === 'expenses' ? 'bg-white text-blue-700 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
            >
              <DollarSign className="w-4 h-4" />
              Catatan Pengeluaran
            </button>
            <button
              type="button"
              onClick={() => setSectionTab('resources')}
              className={`inline-flex items-center justify-center gap-2 rounded-xl px-3 py-2.5 text-sm font-bold transition-all ${sectionTab === 'resources' ? 'bg-white text-indigo-700 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
            >
              <Package className="w-4 h-4" />
              Pemakai Sumber Daya
            </button>
          </div>

          {sectionTab === 'tasks' && (
          <div className="space-y-4">
            <div className="flex flex-col sm:flex-row justify-between sm:items-center gap-3 sm:gap-0">
              <div>
                <h3 className="text-lg font-bold text-slate-800 flex items-center gap-2">
                  <ClipboardCheck className="w-5 h-5 text-emerald-600" />
                  Feed Progress Mobile
                </h3>
                <p className="text-sm text-slate-500 mt-1">
                  {baselineData.tasks.filter((t: any) =>
                    (executionData.taskProgress[t.id] || 0) === 100
                  ).length} dari {baselineData.tasks.length} tugas selesai via mobile
                </p>
              </div>
              <div className={`px-4 py-2 rounded-xl text-sm font-semibold ${baselineData.inventory.every((inv: any) => inv.status === "Ready")
                ? 'bg-emerald-100 text-emerald-700'
                : 'bg-amber-100 text-amber-700'
                }`}>
                {baselineData.inventory.every((inv: any) => inv.status === "Ready")
                  ? '✅ Resources Ready'
                  : '⏳ Resources Pending'}
              </div>
            </div>

            {baselineData.milestones.length === 0 ? (
              <div className="text-center py-12 bg-slate-50 rounded-2xl border-2 border-dashed border-slate-200">
                <AlertCircle className="w-12 h-12 text-slate-400 mx-auto mb-3" />
                <p className="text-slate-500 font-medium">Belum ada data baseline</p>
                <p className="text-sm text-slate-400 mt-1">Selesaikan planning terlebih dahulu</p>
              </div>
            ) : (
              baselineData.milestones.map((m: any) => {
                const tasks = baselineData.tasks.filter(
                  (t: any) => t.milestone_id === m.id
                );
                const milestoneProgress = tasks.reduce((sum: number, t: any) => {
                  const progress = executionData.taskProgress[t.id] || 0;
                  return sum + (progress / 100) * Number(t.weight || 0);
                }, 0);

                return (
                  <div key={m.id} className="space-y-3">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 px-3 py-2 bg-slate-50 rounded-xl">
                      <div className="flex items-center gap-3">
                        <div className={`w-2 h-2 shrink-0 rounded-full ${milestoneProgress === 100 ? 'bg-emerald-500' : 'bg-blue-500'
                          }`} />
                        <span className="font-semibold text-slate-800 line-clamp-1">
                          {m.label.toUpperCase()}
                        </span>
                        <span className="text-xs text-slate-500 shrink-0">
                          Bobot: {Number(m.weight).toFixed(2)}%
                        </span>
                      </div>
                      <div className="flex items-center gap-3 ml-5 sm:ml-0">
                        <span className="text-xs font-medium text-slate-600">
                          {milestoneProgress.toFixed(1)}% complete
                        </span>
                        <div className="w-20 bg-slate-200 rounded-full h-1.5">
                          <div
                            className="h-1.5 rounded-full bg-blue-500 transition-all duration-500"
                            style={{ width: `${milestoneProgress}%` }}
                          />
                        </div>
                      </div>
                    </div>

                    {tasks.map((t: any) => {
                      const progress = executionData.taskProgress[t.id] || 0;
                      const StatusIcon = getTaskStatus(progress).icon;
                      const statusColor = getTaskStatus(progress).color;
                      const isExpanded = expandedTasks.includes(t.id);
                      const teamCount = taskTeams[t.id]?.length || 0;
                      const evidenceCount = (t.evidence || []).length;

                      return (
                        <div
                          key={t.id}
                          className={`bg-white border rounded-2xl transition-all duration-300 ${progress === 100
                            ? 'border-emerald-200 shadow-md shadow-emerald-50'
                            : 'border-slate-200 hover:shadow-lg hover:border-blue-200'
                            }`}
                        >
                          <div
                            className="p-5 cursor-pointer"
                            onClick={() => toggleTaskExpand(t.id)}
                          >
                            <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3">
                              <div className="flex-1">
                                <div className="flex items-center gap-3">
                                  <StatusIcon className={`w-5 h-5 text-${statusColor}-500 shrink-0`} />
                                  <h4 className="font-semibold text-slate-800 leading-tight">
                                    {t.name}
                                  </h4>
                                </div>
                                <div className="flex flex-wrap items-center gap-2 mt-2 sm:ml-8">
                                  <span className={`text-xs font-medium px-2 py-1 rounded-full ${progress === 100
                                    ? 'bg-emerald-100 text-emerald-700'
                                    : progress > 0
                                      ? 'bg-blue-100 text-blue-700'
                                      : 'bg-slate-100 text-slate-600'
                                    }`}>
                                    {getTaskStatus(progress).label}
                                  </span>
                                  <span className="text-xs text-slate-500">
                                    Bobot: {Number(t.weight).toFixed(2)}%
                                  </span>
                                  {teamCount > 0 && (
                                    <span className="text-xs flex items-center gap-1 text-slate-600">
                                      <Users className="w-3 h-3" />
                                      {teamCount} personil
                                    </span>
                                  )}
                                  {evidenceCount > 0 && (
                                    <span className="text-xs flex items-center gap-1 text-slate-600">
                                      <Camera className="w-3 h-3" />
                                      {evidenceCount} bukti
                                    </span>
                                  )}
                                </div>
                              </div>
                              <div className="text-left sm:text-right flex-shrink-0 sm:ml-8 mt-2 sm:mt-0 pl-8 sm:pl-0">
                                <div className="text-lg font-bold text-slate-800">
                                  {Number(progress).toFixed(0)}%
                                </div>
                                <div className="text-xs text-slate-500">
                                  Impact: {(
                                    (((Number(t.weight) * progress) / 100) * Number(m.weight)) / 100
                                  ).toFixed(2)}%
                                </div>
                              </div>
                            </div>

                            <div className="mt-4">
                              <div className="relative h-2 bg-slate-100 rounded-full overflow-hidden">
                                <div
                                  className={`absolute left-0 top-0 h-full rounded-full bg-gradient-to-r ${getProgressColor(progress)} transition-all duration-500`}
                                  style={{ width: `${progress}%` }}
                                />
                              </div>
                              <div className="flex justify-between text-xs text-slate-400 mt-1">
                                <span>0%</span>
                                <span>Read-only dari mobile</span>
                                <span>50%</span>
                                <span>100%</span>
                              </div>
                            </div>

                            <div className="flex items-center justify-between mt-4 pt-3 border-t border-slate-100">
                              <div className="flex items-center gap-2">
                                <button
                                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-slate-600 hover:text-purple-600 hover:bg-purple-50 rounded-lg transition-all"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setActiveEvidence(t.evidence || []);
                                    setIsEvidenceModalOpen(true);
                                  }}
                                >
                                  <Camera className="w-4 h-4" />
                                  Evidence
                                  {evidenceCount > 0 && (
                                    <span className="ml-1 bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded-full text-[10px]">
                                      {evidenceCount}
                                    </span>
                                  )}
                                </button>
                              </div>
                              <ChevronRight className={`w-4 h-4 text-slate-400 transition-transform duration-300 ${isExpanded ? 'rotate-90' : ''
                                }`} />
                            </div>
                          </div>

                          {/* Expanded Content */}
                          {isExpanded && (
                            <div className="px-5 pb-5 pt-2 border-t border-slate-100 bg-slate-50/50 rounded-b-2xl">
                              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <div>
                                  <p className="text-xs font-medium text-slate-500 mb-2">Detail Tugas dari Mobile</p>
                                  <div className="space-y-1 text-sm text-slate-600">
                                    <p>• Milestone: {m.label}</p>
                                    <p>• Bobot Sub-Task: {Number(t.weight).toFixed(2)}%</p>
                                    <p>• Kontribusi Proyek: {(((Number(t.weight) * Number(m.weight)) / 100)).toFixed(2)}%</p>
                                    <p>• Progress dan catatan lapangan dikirim dari aplikasi mobile.</p>
                                  </div>
                                </div>
                                <div>
                                  <p className="text-xs font-medium text-slate-500 mb-2">Aktivitas Terkini</p>
                                  {evidenceCount > 0 ? (
                                    <div className="space-y-1">
                                      <p className="text-sm text-slate-600">
                                        {evidenceCount} bukti kerja tersinkron dari mobile
                                      </p>
                                      <button
                                        className="text-xs text-blue-600 hover:text-blue-700 font-medium flex items-center gap-1"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setActiveEvidence(t.evidence || []);
                                          setIsEvidenceModalOpen(true);
                                        }}
                                      >
                                        <Eye className="w-3 h-3" />
                                        Lihat semua
                                      </button>
                                    </div>
                                  ) : (
                                    <p className="text-sm text-slate-400 italic">Belum ada evidence dari mobile</p>
                                  )}
                                  {teamCount > 0 && (
                                    <div className="mt-2">
                                      <p className="text-xs text-slate-500">
                                        👥 {teamCount} personil ditugaskan
                                      </p>
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                );
              })
            )}
          </div>
          )}

          {sectionTab === 'resources' && (
          <div className="space-y-6">
            {/* Resource Usage */}
            <div className="bg-white border border-slate-200 rounded-2xl p-5 hover:shadow-lg transition-all duration-300">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 bg-indigo-50 rounded-xl">
                  <Package className="w-5 h-5 text-indigo-600" />
                </div>
                <div>
                  <h3 className="font-bold text-slate-800">Pemakai Sumber Daya</h3>
                  <p className="text-xs text-slate-500">Klik item untuk mencatat penggunaan vendor atau inventory</p>
                </div>
              </div>

              {/* Tabs */}
              <div className="flex gap-1 bg-slate-100 rounded-xl p-1 mb-4">
                <button
                  className={`flex-1 px-3 py-1.5 text-xs font-medium rounded-lg transition-all ${activeTab === 'vendors'
                    ? 'bg-white text-slate-800 shadow-sm'
                    : 'text-slate-500 hover:text-slate-700'
                    }`}
                  onClick={() => setActiveTab('vendors')}
                >
                  <Truck className="w-3 h-3 inline mr-1" />
                  Vendor
                </button>
                <button
                  className={`flex-1 px-3 py-1.5 text-xs font-medium rounded-lg transition-all ${activeTab === 'inventory'
                    ? 'bg-white text-slate-800 shadow-sm'
                    : 'text-slate-500 hover:text-slate-700'
                    }`}
                  onClick={() => setActiveTab('inventory')}
                >
                  <Package className="w-3 h-3 inline mr-1" />
                  Inventory
                </button>
              </div>

              <div className="max-h-[420px] overflow-y-auto space-y-2 pr-1">
                {activeTab === 'vendors' ? (
                  baselineData.vendors.length === 0 ? (
                    <div className="text-center py-6 text-slate-400 text-sm">
                      <Truck className="w-8 h-8 mx-auto mb-2 text-slate-300" />
                      Belum ada vendor
                    </div>
                  ) : (
                    baselineData.vendors.map((v: any) => {
                      const isSelected = executionData.usedVendorIds.includes(v.id);
                      return (
                        <div
                          key={v.id}
                          className={`flex items-center gap-3 p-3 rounded-xl transition-all duration-200 ${isExecutionLocked ? 'opacity-70 cursor-not-allowed' : 'cursor-pointer'} ${isSelected
                            ? 'bg-red-50 border-2 border-red-200'
                            : 'bg-slate-50 border-2 border-transparent hover:border-slate-300'
                            }`}
                          onClick={() => !isExecutionLocked && handleToggleMaterial("vendor", v.id, v.cost)}
                        >
                          <div className={`p-2 rounded-xl shrink-0 ${isSelected ? 'bg-red-500 text-white' : 'bg-white text-red-500'
                            }`}>
                            <Truck className="w-4 h-4" />
                          </div>
                          <div className="flex-1 min-w-0 pr-2">
                            <p className={`font-medium text-sm truncate ${isSelected ? 'text-red-600' : 'text-slate-800'
                              }`}>
                              {v.name}
                            </p>
                            <p className="text-xs text-slate-500 truncate">{v.task}</p>
                          </div>
                          <div className="text-right shrink-0 flex flex-col items-end gap-1">
                            <p className="text-[11px] sm:text-xs font-bold text-red-500 whitespace-nowrap">
                              -{new Intl.NumberFormat("id-ID", {
                                style: "currency",
                                currency: "IDR",
                                maximumFractionDigits: 0,
                              }).format(v.cost || 0)}
                            </p>
                            <div className={`w-4 h-4 rounded border-2 flex items-center justify-center transition-all shrink-0 ${isSelected ? 'bg-red-500 border-red-500' : 'border-slate-300'
                              }`}>
                              {isSelected && <Check className="w-3 h-3 text-white" />}
                            </div>
                          </div>
                        </div>
                      );
                    })
                  )
                ) : (
                  baselineData.inventory.length === 0 ? (
                    <div className="text-center py-6 text-slate-400 text-sm">
                      <Package className="w-8 h-8 mx-auto mb-2 text-slate-300" />
                      Belum ada inventaris
                    </div>
                  ) : (
                    baselineData.inventory.map((inv: any) => {
                      const isSelected = executionData.usedInventoryIds.includes(inv.id);
                      return (
                        <div
                          key={inv.id}
                          className={`flex items-center gap-3 p-3 rounded-xl transition-all duration-200 ${isExecutionLocked ? 'opacity-70 cursor-not-allowed' : 'cursor-pointer'} ${isSelected
                            ? 'bg-blue-50 border-2 border-blue-200'
                            : 'bg-slate-50 border-2 border-transparent hover:border-slate-300'
                            }`}
                          onClick={() => !isExecutionLocked && handleToggleMaterial("inventory", inv.id)}
                        >
                          <div className={`p-2 rounded-xl shrink-0 ${isSelected ? 'bg-blue-600 text-white' : 'bg-white text-blue-600'
                            }`}>
                            <Package className="w-4 h-4" />
                          </div>
                          <div className="flex-1 min-w-0 pr-2">
                            <p className={`font-medium text-sm truncate ${isSelected ? 'text-blue-600' : 'text-slate-800'
                              }`}>
                              {inv.name}
                            </p>
                            <p className="text-xs text-slate-500">Tools / Equipment</p>
                          </div>
                          <div className={`w-4 h-4 rounded border-2 flex items-center justify-center transition-all shrink-0 ${isSelected ? 'bg-blue-600 border-blue-600' : 'border-slate-300'
                            }`}>
                            {isSelected && <Check className="w-3 h-3 text-white" />}
                          </div>
                        </div>
                      );
                    })
                  )
                )}
              </div>

              <div className="mt-4 pt-3 border-t border-slate-200">
                <div className="flex justify-between text-sm">
                  <span className="text-slate-500">Total Terpakai</span>
                  <span className={`font-bold ${totalCalculatedExpense > totalBudget ? 'text-red-600' : 'text-slate-800'
                    }`}>
                    {new Intl.NumberFormat("id-ID", {
                      style: "currency",
                      currency: "IDR",
                      maximumFractionDigits: 0,
                    }).format(totalCalculatedExpense)}
                  </span>
                </div>
              </div>
            </div>
          </div>
          )}

          {sectionTab === 'expenses' && (
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
            {/* === LAPORAN REALISASI BIAYA PER KATEGORI === */}
            <div className="bg-white border border-slate-200 rounded-2xl p-5 hover:shadow-lg transition-all duration-300">
              <div className="flex items-center gap-3 mb-5">
                <div className="p-2 bg-blue-50 rounded-xl">
                  <BarChart3 className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <h3 className="font-bold text-slate-800">Laporan Realisasi Biaya per Kategori</h3>
                  <p className="text-xs text-slate-500">Anggaran RAB vs Pengeluaran Aktual yang tercatat</p>
                </div>
              </div>

              {(baselineData.allocation || []).length === 0 ? (
                <p className="text-sm text-slate-400 text-center py-4">Belum ada alokasi RAB yang diatur di tahap Perencanaan.</p>
              ) : (
                <div className="space-y-4">
                  {(baselineData.allocation || []).map((alloc: any) => {
                    const anggaran = (baselineData.budget * alloc.percentage) / 100;
                    const realisasi = (executionData.actualExpenses || [])
                      .filter((e: any) => expenseMatchesAllocation(e.category, alloc))
                      .reduce((sum: number, e: any) => sum + (e.amount || 0), 0);
                    const sisa = anggaran - realisasi;
                    const persen = anggaran > 0 ? (realisasi / anggaran) * 100 : 0;
                    const isOverBudget = realisasi > anggaran;
                    const isWarning = !isOverBudget && persen > 90;

                    return (
                      <div key={alloc.id} className="space-y-1.5">
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-semibold text-slate-700">{alloc.name}</span>
                          <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${isOverBudget
                              ? 'bg-red-100 text-red-700'
                              : isWarning
                                ? 'bg-amber-100 text-amber-700'
                                : 'bg-emerald-100 text-emerald-700'
                            }`}>
                            {persen.toFixed(1)}% terserap
                          </span>
                        </div>
                        {/* Progress bar */}
                        <div className="w-full bg-slate-100 rounded-full h-2.5">
                          <div
                            className={`h-2.5 rounded-full transition-all duration-500 ${isOverBudget
                                ? 'bg-red-500'
                                : isWarning
                                  ? 'bg-amber-400'
                                  : 'bg-emerald-500'
                              }`}
                            style={{ width: `${Math.min(persen, 100)}%` }}
                          />
                        </div>
                        <div className="flex justify-between text-[11px] text-slate-500">
                          <span>
                            Realisasi:{" "}
                            <span className={`font-bold ${isOverBudget ? 'text-red-600' : isWarning ? 'text-amber-600' : 'text-slate-700'}`}>
                              {new Intl.NumberFormat("id-ID", { style: "currency", currency: "IDR", maximumFractionDigits: 0 }).format(realisasi)}
                            </span>
                          </span>
                          <span>
                            Anggaran:{" "}
                            <span className="font-semibold text-slate-600">
                              {new Intl.NumberFormat("id-ID", { style: "currency", currency: "IDR", maximumFractionDigits: 0 }).format(anggaran)}
                            </span>
                            {isOverBudget && (
                              <span className="ml-1 text-red-600 font-bold">
                                (OVER {new Intl.NumberFormat("id-ID", { style: "currency", currency: "IDR", maximumFractionDigits: 0 }).format(Math.abs(sisa))})
                              </span>
                            )}
                          </span>
                        </div>
                      </div>
                    );
                  })}

                  {/* Baris "Lainnya" jika ada */}
                  {(() => {
                    const allocations = baselineData.allocation || [];
                    const lainnya = (executionData.actualExpenses || [])
                      .filter((e: any) => {
                        if (looksLikeContingencyCategory(e.category)) return false;
                        return !allocations.some((alloc: any) => expenseMatchesAllocation(e.category, alloc));
                      })
                      .reduce((sum: number, e: any) => sum + (e.amount || 0), 0);
                    if (lainnya <= 0) return null;
                    return (
                      <div className="pt-2 border-t border-slate-100">
                        <div className="flex justify-between items-center text-[11px]">
                          <span className="text-slate-500">Pengeluaran Lainnya / Tidak Dikategorikan</span>
                          <span className="font-bold text-slate-600">
                            {new Intl.NumberFormat("id-ID", { style: "currency", currency: "IDR", maximumFractionDigits: 0 }).format(lainnya)}
                          </span>
                        </div>
                      </div>
                    );
                  })()}
                </div>
              )}
            </div>

            {/* Catat Pengeluaran Biaya */}
            <div className="bg-white border border-slate-200 rounded-2xl p-4 sm:p-5 hover:shadow-lg transition-all duration-300 flex flex-col h-full">
              <div className="flex items-center gap-3.5 mb-4">
                <div className="p-2.5 bg-blue-50 rounded-xl shrink-0">
                  <DollarSign className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <h3 className="font-bold text-slate-800 text-base sm:text-lg leading-tight">Pencatatan pengeluaran</h3>
                  <p className="text-xs sm:text-sm text-slate-500 mt-0.5">Pos RAB dan dana darurat terpisah. Tidak dikirim ke owner untuk disetujui.</p>
                </div>
              </div>

              <div className="mb-4 grid grid-cols-2 rounded-xl border border-slate-200 bg-slate-50 p-1">
                <button
                  type="button"
                  onClick={() => setExpenseKindTab('operational')}
                  className={`rounded-lg px-3 py-2 text-xs font-bold transition-all ${expenseKindTab === 'operational' ? 'bg-white text-blue-700 shadow-sm' : 'text-slate-500'}`}
                >
                  Pengeluaran RAB
                </button>
                <button
                  type="button"
                  onClick={() => setExpenseKindTab('contingency')}
                  className={`rounded-lg px-3 py-2 text-xs font-bold transition-all ${expenseKindTab === 'contingency' ? 'bg-white text-amber-800 shadow-sm' : 'text-slate-500'}`}
                >
                  Kontingensi
                </button>
              </div>

              {expenseKindTab === 'operational' && (
                <div className="flex flex-col gap-5 flex-grow">
                  {isRabFormLocked && (
                    <span className="self-start px-2.5 py-1 bg-slate-100 text-slate-500 text-[10px] uppercase font-bold rounded-lg border border-slate-200">Terkunci</span>
                  )}
                  {selectedRabAlloc && (
                    <div className="rounded-xl border border-blue-100 bg-blue-50 px-4 py-3">
                      <p className="text-[10px] font-extrabold uppercase tracking-wider text-blue-800">Sisa anggaran pos {selectedRabName}</p>
                      <p className="mt-1 text-lg font-black text-blue-900">{formatIdr(selectedRabRemaining)}</p>
                      <p className="mt-0.5 text-[11px] font-medium text-blue-800/80">
                        Terpakai {formatIdr(selectedRabSpent)} dari {formatIdr(selectedRabBudget)}
                      </p>
                    </div>
                  )}
                  {isRabFormLocked && rabLockReason && (
                    <div className="flex items-start gap-2 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5 text-xs text-slate-600">
                      <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" />
                      <span>{rabLockReason}</span>
                    </div>
                  )}
                  {isRabOverBudget && (
                    <p className="text-xs font-bold text-red-600">Melebihi anggaran pos ini, tetap bisa disimpan.</p>
                  )}
                  <fieldset disabled={isRabFormLocked} className="grid grid-cols-1 gap-3.5 bg-slate-50/50 p-4 rounded-xl border border-slate-100 disabled:opacity-60">
                    <select
                      className="w-full border border-slate-300 rounded-xl px-3.5 py-2.5 text-sm outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 bg-white font-semibold"
                      value={selectedRabName}
                      onChange={(e) => setRabCategory(e.target.value)}
                    >
                      {operationalAllocations.map((alloc: any) => (
                        <option key={alloc.id || allocationName(alloc)} value={allocationName(alloc)}>
                          {allocationName(alloc)}
                        </option>
                      ))}
                    </select>
                    <div className="grid grid-cols-1 sm:grid-cols-12 gap-3.5">
                      <input
                        type="text"
                        className="sm:col-span-7 md:col-span-8 border border-slate-300 rounded-xl px-3.5 py-2.5 text-sm outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 bg-white"
                        placeholder="Judul (misal: Gaji mingguan)"
                        value={rabLabel}
                        onChange={(e) => setRabLabel(e.target.value)}
                      />
                      <div className="sm:col-span-5 md:col-span-4 relative">
                        <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 text-sm font-medium">Rp</span>
                        <input
                          type="text"
                          className="w-full border border-slate-300 rounded-xl pl-9 pr-3.5 py-2.5 text-sm outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 bg-white font-medium"
                          placeholder="0"
                          value={rabAmount ? new Intl.NumberFormat("id-ID").format(Number(rabAmount)) : ""}
                          onChange={(e) => setRabAmount(e.target.value.replace(/[^0-9]/g, ""))}
                        />
                      </div>
                    </div>
                    <input
                      type="date"
                      className="w-full border border-slate-300 rounded-xl px-3.5 py-2.5 text-sm outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 bg-white"
                      value={rabDate}
                      onChange={(e) => setRabDate(e.target.value)}
                    />
                    <textarea
                      className="w-full border border-slate-300 rounded-xl px-3.5 py-2.5 text-sm outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 resize-none bg-white"
                      placeholder="Catatan pengeluaran pos RAB..."
                      rows={2}
                      value={rabDesc}
                      onChange={(e) => setRabDesc(e.target.value)}
                    />
                    <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 pt-1">
                      <div className="flex-grow">
                        <input
                          type="file"
                          accept="image/jpeg,image/png,image/jpg,application/pdf"
                          className="w-full text-sm text-slate-500 file:mr-3 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-xs file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100 cursor-pointer"
                          onChange={(e) => {
                            if (e.target.files && e.target.files[0]) setRabReceipt(e.target.files[0]);
                          }}
                        />
                        <p className="mt-1 text-[11px] text-slate-400">Struk opsional</p>
                      </div>
                      <button
                        type="button"
                        className="w-full sm:w-auto px-5 py-2.5 bg-blue-600 text-white rounded-xl hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 text-sm font-semibold shrink-0"
                        onClick={handleAddRabExpense}
                        disabled={isRabFormLocked || !rabLabel || !rabAmount || !rabDesc || Number(rabAmount) <= 0 || !selectedRabName}
                      >
                        <Plus className="w-4 h-4" />
                        <span>Catat pengeluaran RAB</span>
                      </button>
                    </div>
                  </fieldset>
                  <div className="flex flex-col gap-2 mt-2">
                    <h4 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-1">Pengeluaran RAB</h4>
                    <div className="max-h-[240px] overflow-y-auto space-y-2.5 pr-1.5 custom-scrollbar">
                      {operationalHistory.map((exp: any) => (
                        <div key={exp.id} className="flex flex-col sm:flex-row sm:items-center justify-between p-3.5 bg-white rounded-xl border border-slate-200 gap-3">
                          <div className="flex-1 min-w-0">
                            <span className="text-sm font-bold text-slate-700 truncate block">{exp.title || exp.description?.split(' - ')[0] || exp.label}</span>
                            <span className="text-xs text-slate-500 truncate block mt-0.5">{exp.description}</span>
                            <span className="mt-2 inline-block text-[10px] font-bold px-2.5 py-1 rounded-md bg-blue-50 text-blue-700 border border-blue-100">{exp.category}</span>
                          </div>
                          <div className="flex items-center shrink-0 gap-2">
                            {exp.receipt_url ? (
                              <button
                                type="button"
                                onClick={() => setReceiptPreview({
                                  url: exp.receipt_url,
                                  title: exp.title || exp.description || 'Struk pengeluaran RAB',
                                })}
                                className="inline-flex items-center gap-1 text-[11px] font-bold text-blue-700 bg-blue-50 border border-blue-100 px-2.5 py-1.5 rounded-lg"
                              >
                                <FileText className="w-3.5 h-3.5" /> Struk
                              </button>
                            ) : (
                              <span className="text-[11px] font-medium text-slate-400">Tidak ada struk</span>
                            )}
                            <span className="text-sm font-bold text-red-500 bg-red-50 px-3 py-1.5 rounded-lg border border-red-100">-{formatIdr(exp.amount)}</span>
                          </div>
                        </div>
                      ))}
                      {operationalHistory.length === 0 && (
                        <div className="text-center py-8 px-4 bg-slate-50 rounded-xl border border-dashed border-slate-200">
                          <p className="text-sm font-medium text-slate-500">Belum ada pengeluaran RAB</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {expenseKindTab === 'contingency' && (
              <>
              {isContingencyFormLocked && (
                <span className="mb-3 self-start px-2.5 py-1 bg-slate-100 text-slate-500 text-[10px] uppercase font-bold rounded-lg border border-slate-200">Terkunci</span>
              )}
              <div className="mb-4 rounded-xl border border-amber-100 bg-amber-50 px-4 py-3">
                <p className="text-[10px] font-extrabold uppercase tracking-wider text-amber-800">Sisa plafon kontingensi</p>
                <p className="mt-1 text-lg font-black text-amber-900">{formatIdr(remainingContingency)}</p>
                <p className="mt-0.5 text-[11px] font-medium text-amber-800/80">
                  Terpakai {formatIdr(spentContingency)} dari {formatIdr(allocatedContingency)}
                </p>
              </div>

              {isContingencyFormLocked && contingencyLockReason && (
                <div className="mb-4 flex items-start gap-2 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5 text-xs text-slate-600">
                  <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" />
                  <span>{contingencyLockReason}</span>
                </div>
              )}

              {/* Form Section */}
              <div className="flex flex-col gap-5 flex-grow">
                <fieldset disabled={isContingencyFormLocked} className="grid grid-cols-1 gap-3.5 bg-slate-50/50 p-4 rounded-xl border border-slate-100 disabled:opacity-60">
                    <div className="rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 text-sm">
                      <span className="text-[10px] font-extrabold uppercase tracking-wider text-slate-400">Kategori</span>
                      <p className="font-bold text-slate-800">Kontingensi</p>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-12 gap-3.5">
                      <input
                        type="text"
                        className="sm:col-span-7 md:col-span-8 border border-slate-300 rounded-xl px-3.5 py-2.5 text-sm outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all bg-white"
                        placeholder="Judul (misal: Beli batu bata)"
                        value={expenseLabel}
                        onChange={(e) => setExpenseLabel(e.target.value)}
                      />
                      <div className="sm:col-span-5 md:col-span-4 relative">
                        <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 text-sm font-medium">Rp</span>
                        <input
                          type="text"
                          className="w-full border border-slate-300 rounded-xl pl-9 pr-3.5 py-2.5 text-sm outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all bg-white font-medium"
                          placeholder="0"
                          value={expenseAmount ? new Intl.NumberFormat("id-ID").format(Number(expenseAmount)) : ""}
                          onChange={(e) => {
                            const value = e.target.value.replace(/[^0-9]/g, "");
                            setExpenseAmount(value);
                          }}
                        />
                      </div>
                    </div>

                    <input
                      type="date"
                      className="w-full border border-slate-300 rounded-xl px-3.5 py-2.5 text-sm outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all bg-white"
                      value={expenseDate}
                      onChange={(e) => setExpenseDate(e.target.value)}
                    />

                    <textarea
                      className="w-full border border-slate-300 rounded-xl px-3.5 py-2.5 text-sm outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all resize-none bg-white"
                      placeholder="Alasan pemakaian dana darurat..."
                      rows={2}
                      value={expenseDesc}
                      onChange={(e) => setExpenseDesc(e.target.value)}
                    />

                    <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 pt-1">
                      <div className="flex-grow">
                        <input
                          type="file"
                          accept="image/jpeg,image/png,image/jpg,application/pdf"
                          className="w-full text-sm text-slate-500 file:mr-3 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-xs file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100 cursor-pointer"
                          onChange={(e) => {
                            if (e.target.files && e.target.files[0]) {
                              setExpenseReceipt(e.target.files[0]);
                            }
                          }}
                        />
                      </div>
                      <button
                        type="button"
                        className="w-full sm:w-auto px-5 py-2.5 bg-blue-600 text-white rounded-xl hover:bg-blue-700 active:bg-blue-800 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 text-sm font-semibold shrink-0"
                        onClick={handleAddExpense}
                        disabled={isContingencyFormLocked || !expenseLabel || !expenseAmount || !expenseDesc || !expenseReceipt || Number(expenseAmount) <= 0 || Number(expenseAmount) > remainingContingency}
                      >
                        <Plus className="w-4 h-4" />
                        <span>Catat Biaya</span>
                      </button>
                    </div>
                </fieldset>

                {/* List Pengeluaran */}
                <div className="flex flex-col gap-2 mt-2">
                  <h4 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-1">Pemakaian kontingensi</h4>

                  <div className="max-h-[240px] overflow-y-auto space-y-2.5 pr-1.5 custom-scrollbar">
                    {contingencyHistory.map((exp: any) => {
                      const kategori = (baselineData.allocation || []).find(
                        (a: any) => expenseMatchesAllocation(exp.category, a)
                      );

                      return (
                        <div
                          key={exp.id}
                          className="flex flex-col sm:flex-row sm:items-center justify-between p-3.5 bg-white rounded-xl border border-slate-200 hover:border-blue-300 hover:shadow-sm transition-all gap-3 group"
                        >
                          <div className="flex-1 min-w-0">
                            <span className="text-sm font-bold text-slate-700 truncate block">
                              {exp.title || exp.description?.split(' - ')[0] || exp.label}
                            </span>
                            <span className="text-xs text-slate-500 truncate block mt-0.5">
                              {exp.description}
                            </span>

                            <div className="mt-2">
                              <span className="text-[10px] font-bold px-2.5 py-1 rounded-md bg-amber-50 text-amber-700 border border-amber-100">
                                {kategori?.name || exp.category || "Kontingensi"}
                              </span>
                            </div>
                          </div>

                          <div className="flex items-center sm:justify-end shrink-0 gap-2 pt-2 sm:pt-0 border-t border-slate-100 sm:border-0">
                            {exp.receipt_url ? (
                              <button
                                type="button"
                                onClick={() => setReceiptPreview({
                                  url: exp.receipt_url,
                                  title: exp.title || exp.description || 'Struk kontingensi',
                                })}
                                className="inline-flex items-center gap-1 text-[11px] font-bold text-blue-700 bg-blue-50 border border-blue-100 px-2.5 py-1.5 rounded-lg hover:bg-blue-100"
                              >
                                <FileText className="w-3.5 h-3.5" />
                                Lihat struk
                              </button>
                            ) : (
                              <span className="text-[11px] font-medium text-slate-400">Tidak ada struk</span>
                            )}
                            <span className="text-sm font-bold text-red-500 bg-red-50 px-3 py-1.5 rounded-lg border border-red-100">
                              -{new Intl.NumberFormat("id-ID", {
                                style: "currency",
                                currency: "IDR",
                                maximumFractionDigits: 0,
                              }).format(exp.amount)}
                            </span>
                          </div>
                        </div>
                      );
                    })}

                    {/* Empty State */}
                    {contingencyHistory.length === 0 && (
                      <div className="text-center py-8 px-4 bg-slate-50 rounded-xl border border-slate-200 border-dashed">
                        <div className="w-12 h-12 bg-white shadow-sm rounded-full flex items-center justify-center mx-auto mb-3">
                          <DollarSign className="w-5 h-5 text-slate-400" />
                        </div>
                        <p className="text-sm font-medium text-slate-500">
                          Belum ada pemakaian kontingensi
                        </p>
                        <p className="text-xs text-slate-400 mt-1">
                          Catat biaya tak terduga melalui form di atas.
                        </p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Total Expenses Footer */}
                {contingencyHistory.length > 0 && (
                  <div className="mt-auto pt-4 border-t border-slate-200 flex flex-wrap justify-between items-center gap-2">
                    <span className="text-sm font-medium text-slate-600">Total kontingensi terpakai:</span>
                    <span className="text-base sm:text-lg font-bold text-red-600">
                      {formatIdr(
                        spentContingency || contingencyHistory.reduce((sum: number, e: any) => sum + Number(e.amount || 0), 0),
                      )}
                    </span>
                  </div>
                )}
              </div>
              </>
              )}
            </div>
          </div>
          )}
        </div>
      </div>
    </ProjectSectionShell>
      <ReceiptPreviewModal receipt={receiptPreview} onClose={() => setReceiptPreview(null)} />
    </>
  );
};

export default ExecutionSection;

